// @flow
import React, { useEffect, useState } from 'react'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { ArticleData } from '@ta-interaktiv/react-article-teasers'
import styles from './articleLink.m.scss'
import {
  getAuthors,
  getLead,
  getPublishingDate,
  getTeaserImgSrc,
  getTitle,
  getTitleHeader,
  getUpdatedDate
} from '@ta-interaktiv/api-access'
import { useGesture } from '@use-gesture/react'
import { useSpring, animated } from '@react-spring/web'

type Timestamp = number

interface ArticleTeaserData {
  author: string[]
  imageSrc: string
  lead: string
  title: string
  titleHeader: string
  publishingDate: Timestamp
  updateDate: Timestamp
  id: string | number
}

/**
 * Dynamic inline teaser Function.
 * This function allows to render one or several article teasers.
 *
 * @param {string} title The title displayed on the bloc containing the teasers. "Lire aussi:" is the default value.
 * @param {string[]} ids The list of articles' ids.
 */
export const ArticleTeaser = ({
  title,
  ids,
  inverted,
  sep = false,
  handleClick,
  handleArticleLoaded,
  readArticles
}: {
  title?: string
  ids: string[]
  inverted: boolean
  sep: boolean
  handleClick: (id: string | number) => void
  handleArticleLoaded: () => void
  readArticles: number[] | string[]
}) => {
  const [limit, setLimit] = useState(4)
  const isMobile = window.innerWidth < 768
  const domain = parseHostname().publicationName
  const [articles, setArticles] = useState(
    new Map<string | number, ArticleData>()
  )
  const [page, setPage] = useState(0)
  const [allowDrag, setAllowDrag] = useState(false)

  const getArticleTeaserData = (
    domain: string,
    articleId: string | number
  ): Promise<ArticleTeaserData> => {
    return Promise.all([
      getAuthors(domain, articleId),
      getTeaserImgSrc(domain, articleId, '800px'),
      getLead(domain, articleId),
      getTitle(domain, articleId),
      getTitleHeader(domain, articleId),
      getPublishingDate(domain, articleId),
      getUpdatedDate(domain, articleId)
    ])
      .then(
        ([
          authors,
          teaserImgSrc,
          lead,
          title,
          titleHeader,
          publishingDateAsIsoString,
          updatedDateAsIsoString
        ]: [string[], string, string, string, string, string, string]) => {
          return {
            authors: authors.join(', '),
            imageSrc: teaserImgSrc,
            lead: lead ?? '',
            title: title ?? '',
            titleHeader: titleHeader,
            publishingDate: new Date(publishingDateAsIsoString).getTime(),
            updateDate: new Date(updatedDateAsIsoString).getTime(),
            id: articleId
          }
        }
      )
      .catch((e: Error) => {
        console.warn(
          `Article with ID ${articleId} could not be loaded, due to ${e.name}: ${e.message}`
        )
      })
  }
  const [{ x }, api] = useSpring(() => ({ x: 0 }))

  const bind = useGesture(
    {
      onDrag: ({ down, swipe: [swipeX], movement: [mx, my] }) => {
        const releasePos = (page * -window.innerWidth * 65) / 100

        if (allowDrag) {
          if (swipeX !== 0) {
            setAllowDrag(false)
            const newPage = page + -swipeX
            if (
              (page > 0 && swipeX > 0) ||
              (page < articles.size - 1 && swipeX < 0)
            ) {
              setPage(newPage)
            }
          }
        }

        if (allowDrag) {
          api.start({ x: down ? mx + releasePos : releasePos, immediate: down })
        }
      },
      onDragStart: state => {
        setAllowDrag(true)
      },
      onDragEnd: state => {
        setAllowDrag(true)
      }
    },
    {
      swipe: {
        distance: [30, 30]
      }
    }
  )

  useEffect(() => {
    if (articles.size > 0) {
      const releasePos = (page * -window.innerWidth * 65) / 100
      api.start({ x: releasePos })
    }
  }, [page])

  useEffect(() => {
    ids.forEach(articleId => {
      getArticleTeaserData(domain, articleId).then(
        (articleTeaserData: ArticleData) => {
          if (articleTeaserData) {
            setArticles(
              articles =>
                new Map(
                  [...articles.set(articleId, articleTeaserData)].sort(
                    (a, b) => b[1].updateDate - a[1].updateDate
                  )
                )
            )
            handleArticleLoaded(articleTeaserData)
          }
        }
      )
    })
  }, [])

  return (
    <>
      {articles.size > 0 &&
        (isMobile ? (
          <div
            className={`${styles.container} ${
              inverted ? styles.containerInverted : ''
            } ${sep ? styles.containerSep : ''}`}
          >
            <div className={styles.content}>
              <div className={styles.header}>{title}</div>
              <div className={styles.articleTitle}>
                <div className={styles.articleNum}>{page + 1}</div>
                <a
                  onClick={e => {
                    e.preventDefault()
                    handleClick([...articles.values()][page].id)
                    window.open(
                      `https://www.${domain}.ch/${
                        [...articles.values()][page].id
                      }`,
                      '_blank'
                    )
                  }}
                  href={`https://www.${domain}.ch/${
                    [...articles.values()][page].id
                  }`}
                >
                  {[...articles.values()][page].title}
                </a>
                <div>
                  <a
                    onClick={e => {
                      e.preventDefault()
                      handleClick([...articles.values()][page].id)
                      window.open(
                        `https://www.${domain}.ch/${
                          [...articles.values()][page].id
                        }`,
                        '_blank'
                      )
                    }}
                    href={`https://www.${domain}.ch/${
                      [...articles.values()][page].id
                    }`}
                  >
                    <div className={styles.button}>
                      {readArticles.includes(
                        [...articles.values()][page].id
                      ) ? (
                        <>
                          Déjà lu{' '}
                          <svg
                            width='16px'
                            height='16px'
                            viewBox='0 0 16 16'
                            xmlns='http://www.w3.org/2000/svg'
                          >
                            <path
                              fill='none'
                              strokeWidth='1.5'
                              stroke='#ffffff'
                              d='m3 8 4 4 6-9'
                            />
                          </svg>
                        </>
                      ) : (
                        <>Lire l'article</>
                      )}
                    </div>
                  </a>
                </div>
              </div>
              <animated.div className={styles.list} {...bind()} style={{ x }}>
                {[...articles.values()].map((article: ArticleData) => (
                  <a
                    className={styles.teaserContainer}
                    key={`article-teaser-${article.id}`}
                    href={`https://www.${domain}.ch/${article.id}`}
                    onClick={e => {
                      e.preventDefault()

                      if ([...articles.values()][page].id === article.id) {
                        console.log('OK')
                      } else {
                        setPage(page + 1)
                        return 0
                      }
                      handleClick([...articles.values()][page].id)
                      window.open(
                        `https://www.${domain}.ch/${article.id}`,
                        '_blank'
                      )
                    }}
                  >
                    <div className={styles.imageContainer}>
                      <img
                        className={styles.image}
                        alt=''
                        src={article.imageSrc}
                      />
                    </div>
                  </a>
                ))}
              </animated.div>
              <div className={styles.paginationHelp}>
                ◄ Swipez pour en voir plus ►
              </div>
              {/* <div className={styles.paginationBlock}>
                {[...articles.values()].map((article: ArticleData, index) => (
                  <div
                    key={`page-bullet-${article.id}`}
                    className={`${styles.pageBullet} ${
                      index === page ? styles.pageBulletActive : ''
                    }`}
                  />
                ))}
              </div> */}
              <div className={styles.paginationLineContainer}>
                <div className={styles.paginationLineContent}>
                  <div
                    className={styles.paginationLine}
                    style={{
                      width:
                        ((page + 1) / [...articles.values()].length) * 100 + '%'
                    }}
                  />
                </div>
              </div>
              {/* <div>{page + 1} - {[...articles.values()].length}</div> */}
            </div>
          </div>
        ) : (
          <div
            className={`${styles.cardsContainer} ${
              inverted ? styles.containerInverted : ''
            } ${sep ? styles.containerSep : ''}`}
          >
            <div className={styles.header}>{title}</div>
            <div className={styles.gridContainer}>
              {[...articles.values()]
                .slice(0, limit)
                .map((article: ArticleData) => (
                  <a
                    className={styles.cardContainer}
                    key={`article-teaser-${article.id}`}
                    href={`https://www.${domain}.ch/${article.id}`}
                    onClick={e => {
                      e.preventDefault()
                      handleClick(article.id)
                      window.open(
                        `https://www.${domain}.ch/${article.id}`,
                        '_blank'
                      )
                    }}
                  >
                    <div className={styles.cardImageContainer}>
                      <img
                        className={styles.cardImage}
                        alt=''
                        src={article.imageSrc}
                      />
                    </div>
                    <div className={styles.cardContent}>
                      <div>{article.title}</div>
                    </div>
                    <div className={styles.cardButton}>
                      <div className={styles.button}>
                        {readArticles.includes(+article.id) ? (
                          <>
                            Déjà lu{' '}
                            <svg
                              width='16px'
                              height='16px'
                              viewBox='0 0 16 16'
                              xmlns='http://www.w3.org/2000/svg'
                            >
                              <path
                                fill='none'
                                strokeWidth='1.5'
                                stroke='var(--stda-main-color)'
                                d='m3 8 4 4 6-9'
                              />
                            </svg>
                          </>
                        ) : (
                          <>Lire l'article</>
                        )}
                      </div>
                    </div>
                  </a>
                ))}
            </div>
            {limit < [...articles.values()].length && (
              <div
                className='ui vertical very fitted segment'
                style={{ paddingBottom: 0 }}
              >
                <div className='ui center aligned text container'>
                  <div
                    className={`${styles.button} ${styles.buttonBig}`}
                    onClick={() => {
                      setLimit(limit + 4)
                    }}
                  >
                    Plus de questions
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
    </>
  )
}
